import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { DICTATION_COMMAND_GROUPS } from './dictationCommands';

// Compact "voice commands" cheat-sheet shown next to the mic button.
// Reused by the Note body editor and the Task detail comment box.
//
// NOTE: the Popover portals to document.body by default, which lives OUTSIDE
// the `.notes-scope` theme wrapper — so the scoped `--n-*` CSS variables (and
// dark-mode values) would not resolve, leaving the panel transparent/illegible.
// We therefore portal the content INTO the nearest `.notes-scope` element so it
// inherits the correct surface colour, text colour and dark/light palette.
export function DictationHint({ triggerClassName = '', side = 'bottom', align = 'start' }) {
  const triggerRef = useRef(null);
  const [container, setContainer] = useState(null);

  useEffect(() => {
    const scope = triggerRef.current ? triggerRef.current.closest('.notes-scope') : null;
    setContainer(scope || null);
  }, []);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          title="Voice commands you can speak"
          aria-label="Voice command help"
          data-testid="dictation-help-button"
          className={
            triggerClassName ||
            'inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--n-muted-2)] hover:bg-[var(--n-canvas-2)] hover:text-[var(--n-ink)] transition-colors n-focus'
          }
        >
          <HelpCircle size={15} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        container={container}
        side={side}
        align={align}
        sideOffset={8}
        className="z-[350] w-72 rounded-xl border border-[var(--n-border)] p-3 text-[var(--n-ink)] shadow-[var(--n-shadow)]"
        style={{ background: 'var(--n-surface)' }}
        data-testid="dictation-help-popover"
      >
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
          Say these while dictating
        </div>
        <div className="space-y-2.5">
          {DICTATION_COMMAND_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--n-accent)]">
                {group.title}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.items.map((item) => (
                  <span
                    key={item.say}
                    className="inline-flex items-center gap-1 rounded-md border border-[var(--n-line)] bg-[var(--n-canvas-2)] px-1.5 py-0.5 text-[11.5px] text-[var(--n-ink-soft)]"
                    data-testid="dictation-help-command"
                  >
                    <span className="font-medium text-[var(--n-ink)]">“{item.say}”</span>
                    <span className="text-[var(--n-muted-2)]">→</span>
                    <span className="font-semibold text-[var(--n-accent)]">{item.gives}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DictationHint;
